import { AlertTriangle, Loader2 } from 'lucide-react';
import type { CanvasClass } from '../types/creative-template.types';
import type { DesignerCanvasClass, DesignerExemplar, ExtractionStatus } from '../types/designer-exemplar.types';
import { CANVAS_ASPECT_RATIOS } from '../helpers/creative-template.helpers';
import { canvasClassLabel, isDisplayableImageUrl, normaliseDesignerCanvasClasses, normaliseDesignerExemplars } from '../helpers/designer-exemplar.helpers';
import { DesignerExemplarStatusBadge } from './designer-exemplar-status-badge';

export type DesignerExemplarActiveFilter = 'active' | 'retired' | 'all';

export interface DesignerExemplarListFilters {
  canvasClass: CanvasClass | 'all';
  vertical: string | 'all';
  extractionStatus: ExtractionStatus | 'all';
  active: DesignerExemplarActiveFilter;
}

interface DesignerExemplarListProps {
  exemplars: DesignerExemplar[] | undefined;
  canvasClasses: DesignerCanvasClass[] | undefined;
  filters: DesignerExemplarListFilters;
  isLoading: boolean;
  isError: boolean;
  onSelect: (exemplar: DesignerExemplar) => void;
  onFiltersChange: (filters: DesignerExemplarListFilters) => void;
  verticalOptions: string[];
}

function filterExemplars(
  exemplars: DesignerExemplar[],
  filters: DesignerExemplarListFilters,
): DesignerExemplar[] {
  return exemplars.filter((exemplar) => {
    if (filters.canvasClass !== 'all' && exemplar.canvas_class !== filters.canvasClass) {
      return false;
    }
    if (filters.vertical !== 'all' && exemplar.vertical !== filters.vertical) {
      return false;
    }
    if (filters.extractionStatus !== 'all' && exemplar.extraction_status !== filters.extractionStatus) {
      return false;
    }
    if (filters.active === 'active' && !exemplar.active) {
      return false;
    }
    if (filters.active === 'retired' && exemplar.active) {
      return false;
    }
    return true;
  });
}

function groupByCanvasClass(
  exemplars: DesignerExemplar[],
  canvasClasses: DesignerCanvasClass[] | undefined,
): Array<{ canvasClass: CanvasClass; items: DesignerExemplar[] }> {
  const order = canvasClasses?.map((entry) => entry.slug) ?? [];
  const groups = new Map<CanvasClass, DesignerExemplar[]>();

  exemplars.forEach((exemplar) => {
    const canvasClass = (exemplar.canvas_class || 'panel_square') as CanvasClass;
    const existing = groups.get(canvasClass) ?? [];
    existing.push(exemplar);
    groups.set(canvasClass, existing);
  });

  const sortedKeys = [...groups.keys()].sort((left, right) => {
    const leftIndex = order.indexOf(left);
    const rightIndex = order.indexOf(right);
    if (leftIndex === -1 && rightIndex === -1) {
      return left.localeCompare(right);
    }
    if (leftIndex === -1) {
      return 1;
    }
    if (rightIndex === -1) {
      return -1;
    }
    return leftIndex - rightIndex;
  });

  return sortedKeys.map((canvasClass) => ({
    canvasClass,
    items: groups.get(canvasClass) ?? [],
  }));
}

export function DesignerExemplarList({
  exemplars,
  canvasClasses,
  filters,
  isLoading,
  isError,
  onSelect,
  onFiltersChange,
  verticalOptions,
}: DesignerExemplarListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500" role="status">
        <Loader2 className="size-4 animate-spin text-gray-500" />
        Loading exemplars…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-md border border-red-101 bg-red-101 p-4 text-sm text-red-700" role="alert">
        Could not load exemplars. You may need staff or admin access.
      </div>
    );
  }

  const filtered = filterExemplars(normaliseDesignerExemplars(exemplars), filters);
  const grouped = groupByCanvasClass(filtered, normaliseDesignerCanvasClasses(canvasClasses));

  return (
    <div className="space-y-4">
      <div className="grid gap-3 rounded-md border border-gray-300 bg-white p-4 md:grid-cols-4">
        <label className="space-y-1 text-sm">
          <span className="font-medium text-gray-700">Canvas class</span>
          <select
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            value={filters.canvasClass}
            onChange={(event) => onFiltersChange({
              ...filters,
              canvasClass: event.target.value as DesignerExemplarListFilters['canvasClass'],
            })}
          >
            <option value="all">All classes</option>
            {(normaliseDesignerCanvasClasses(canvasClasses)).map((entry) => (
              <option key={entry.slug} value={entry.slug}>
                {entry.display_name}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1 text-sm">
          <span className="font-medium text-gray-700">Vertical</span>
          <select
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            value={filters.vertical}
            onChange={(event) => onFiltersChange({ ...filters, vertical: event.target.value })}
          >
            <option value="all">All verticals</option>
            {verticalOptions.map((vertical) => (
              <option key={vertical} value={vertical}>
                {vertical}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1 text-sm">
          <span className="font-medium text-gray-700">Extraction status</span>
          <select
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            value={filters.extractionStatus}
            onChange={(event) => onFiltersChange({
              ...filters,
              extractionStatus: event.target.value as DesignerExemplarListFilters['extractionStatus'],
            })}
          >
            <option value="all">All statuses</option>
            <option value="pending">Pending</option>
            <option value="running">Running</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
          </select>
        </label>
        <label className="space-y-1 text-sm">
          <span className="font-medium text-gray-700">Active state</span>
          <select
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            value={filters.active}
            onChange={(event) => onFiltersChange({
              ...filters,
              active: event.target.value as DesignerExemplarActiveFilter,
            })}
          >
            <option value="active">Active</option>
            <option value="retired">Retired</option>
            <option value="all">All</option>
          </select>
        </label>
      </div>

      {!filtered.length ? (
        <div className="rounded-md border border-gray-400 bg-gray-50 p-4 text-sm text-gray-600">
          No exemplars match the current filters.
        </div>
      ) : (
        grouped.map(({ canvasClass, items }) => (
          <section key={canvasClass} className="space-y-3">
            <h3 className="text-sm font-medium capitalize text-black">
              {canvasClassLabel(canvasClass, canvasClasses)}
            </h3>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((exemplar) => {
                const aspectRatio = CANVAS_ASPECT_RATIOS[exemplar.canvas_class];
                return (
                  <button
                    key={exemplar.id}
                    type="button"
                    onClick={() => onSelect(exemplar)}
                    className="rounded-md border border-gray-300 bg-white p-3 text-left transition hover:border-gray-500"
                  >
                    <div
                      className="relative mb-3 overflow-hidden rounded-md border border-gray-300 bg-gray-100"
                      style={{ aspectRatio }}
                    >
                      {exemplar.image_url && isDisplayableImageUrl(exemplar.image_url) ? (
                        <img
                          src={exemplar.image_url}
                          alt={exemplar.name}
                          className="size-full object-cover"
                        />
                      ) : (
                        <div className="flex size-full items-center justify-center text-xs text-gray-500">
                          No image
                        </div>
                      )}
                      {exemplar.ratio_mismatch ? (
                        <div className="absolute right-2 top-2 rounded-full bg-amber-100 p-1 text-amber-800">
                          <AlertTriangle className="size-3.5" />
                        </div>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <div className="font-medium text-black">{exemplar.name}</div>
                      <div className="text-xs text-gray-500">{exemplar.vertical}</div>
                      {exemplar.image_width && exemplar.image_height ? (
                        <div className="text-xs text-gray-500">
                          {exemplar.image_width} × {exemplar.image_height}px
                        </div>
                      ) : null}
                      <div className="flex flex-wrap items-center gap-2">
                        <DesignerExemplarStatusBadge status={exemplar.extraction_status} />
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            exemplar.active
                              ? 'bg-[rgba(49,187,100,0.15)] text-[#31BB64]'
                              : 'bg-gray-300 text-gray-700'
                          }`}
                        >
                          {exemplar.active ? 'Active' : 'Retired'}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
